import { Col, Space } from "components/primitives";
import { Palette } from "constants/widgets";
import { Color, ColorBox, ColorPalette } from "material-ui-color";
import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { WidgetSettingFC } from "types/Setting";

interface GridState {
  grid: number;
  selectedRange: number;
  range: [number, number];
  selectedColor: string;
  selectedIndex: number | null;
}

interface InfoState {
  noMargin: boolean;
  label: string;
  secondaryLabel: string;
}

export const NoggleSetting: WidgetSettingFC<GridState, InfoState> = (props) => {
  const { state, data, setData, info } = props;
  const pickerRef = useRef<HTMLDivElement | null>(null);

  const [pickerPosition, setPickerPosition] = useState({
    top: 10,
    left: -50,
  });
  const [showPicker, setShowPicker] = useState(false);

  const [bgColor, setBgColor] = useState<Color | string>("#ffffff");

  const { noMargin, label, secondaryLabel } = info;

  // Close picker on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (event.target) {
        const target: any = event.target;
        if (Array.from(target?.classList).includes("grid-item")) {
          return;
        }
      }

      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        setShowPicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const onColorChange = (color: string) => {
    setData({
      ...data,
      selectedColor: `#${color}`,
    });
  };

  const changeGrid = (value: number) => {
    setData({ ...data, grid: value, selectedIndex: null, selectedColor: "" });
  };

  const clear = () => {
    setData({
      grid: data.grid,
      selectedIndex: null,
      selectedColor: "",
      selectedRange: data.selectedRange,
      range: data.range,
    });
  };

  return (
    <OuterContainer noMargin={noMargin}>
      <p className="label">{label}</p>
      <Row>
        <OptionButton selected={data.grid === 3} onClick={() => changeGrid(3)}>
          3x3
        </OptionButton>
        <OptionButton selected={data.grid === 4} onClick={() => changeGrid(4)}>
          4x4
        </OptionButton>
        <OptionButton selected={data.grid === 5} onClick={() => changeGrid(5)}>
          5x5
        </OptionButton>
      </Row>

      <p className="label" style={{ marginTop: 16 }}>
        {secondaryLabel}
      </p>
      <Row>
        <OptionButton
          selected={data.selectedRange === 9}
          onClick={() => setData({ ...data, range: [1, 9], selectedRange: 9 })}
        >
          1-9
        </OptionButton>
        <OptionButton
          selected={data.selectedRange === 20}
          onClick={() => setData({ ...data, range: [1, 20], selectedRange: 20 })}
        >
          1-20
        </OptionButton>
        <OptionButton
          selected={data.selectedRange === 100}
          onClick={() => setData({ ...data, range: [1, 100], selectedRange: 100 })}
        >
          1-100
        </OptionButton>
      </Row>
      <Space gap={16} />
      <p className="label">Color</p>
      {showPicker && (
        <ColorPickerContainer ref={pickerRef} style={pickerPosition} key={state.selectedIndex}>
          <ColorBox
            defaultValue={bgColor}
            onChange={(value) => {
              setBgColor(value);
              onColorChange(value.hex);
            }}
            value={bgColor}
          />
        </ColorPickerContainer>
      )}
      <CustomColorBox
        gradientBgValue="linear-gradient(to right, red, blue)"
        onClick={() => setShowPicker(true)}
      />
      {/* <ColorPalette
        palette={Palette}
        onSelect={(c) => onColorChange(Palette[c])}
      /> */}
      <Space />
      <OptionButton onClick={clear}>Clear</OptionButton>
    </OuterContainer>
  );
};

const CustomColorBox = styled.div<{
  hexBgValue?: string;
  gradientBgValue?: string;
}>`
  width: 100%;
  height: 37px;
  border: 1px solid black;
  border-radius: 3px;
  background: ${(props) =>
    props.gradientBgValue
      ? props.gradientBgValue
      : props.hexBgValue
        ? props.hexBgValue
        : "transparent"};
`;

const ColorPickerContainer = styled.div`
  position: absolute;
  padding: 1rem;
  background: #fff;
  box-shadow: 0 0 5px rgba(0, 9, 43, 0.5);
  z-index: 10;
`;

const OuterContainer = styled(Col)<{ noMargin: boolean }>`
  margin-bottom: ${(props) => (props.noMargin ? "0px" : "35px")};

  .label {
    margin-bottom: 8px;
    font-weight: 600;
    font-size: 12pt;
  }
`;

const OptionButton = styled.button<{ selected?: boolean }>`
  padding: 10px 20px;
  font-size: 16px;
  cursor: pointer;
  border: 2px solid ${(props) => (props.selected ? "#007BFF" : "#ccc")};
  background-color: ${(props) => (props.selected ? "#e7f1ff" : "#f0f0f0")};
  color: ${(props) => (props.selected ? "#007BFF" : "#333")};
  border-radius: 5px;

  &:hover {
    border-color: #007bff;
    background-color: #e7f1ff;
  }
`;

export const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;
